import { AppState } from '../stores/app-state'
import { ApiService } from '../services/api'
import { Toast } from './toast'
import { signOut, type AuthResponse } from '../lib/auth-utils'
import { showUserProfile, updateProfileConnectionStatus } from './user-profile'
import { showSettingsModal } from './settings-modal'
import { logger } from '../utils/logger'
import { authClient } from '../lib/auth-client'

export interface WorkflowStep {
  id: number
  name: string
  label: string
  description: string
  completed: boolean
  locked: boolean
}

export class WorkflowNav {
  private container: HTMLElement
  private appState: AppState
  private apiService: ApiService
  private toastService: Toast
  private currentStep: number = 1
  private isAdvancedMode: boolean = true
  
  // Better Auth session subscription for reactive updates
  private sessionSubscription: (() => void) | null = null
  private classicSteps: WorkflowStep[] = [
    {
      id: 1,
      name: 'project',
      label: 'Projects',
      description: 'Select or create a project',
      completed: false,
      locked: false
    },
    {
      id: 2,
      name: 'input-capture',
      label: 'Input & Capture',
      description: 'Upload PDFs and select text regions',
      completed: false,
      locked: true
    },
    {
      id: 3,
      name: 'description',
      label: 'Descriptions',
      description: 'Clean and normalize text selections',
      completed: false,
      locked: true
    },
    {
      id: 4,
      name: 'plotting',
      label: 'Plot',
      description: 'Generate plots from coordinates',
      completed: false,
      locked: true
    }
  ]
  private standardSteps: WorkflowStep[] = [
    {
      id: 1,
      name: 'project',
      label: 'Projects',
      description: 'Select or create a project',
      completed: false,
      locked: false
    },
    {
      id: 2,
      name: 'input-capture',
      label: 'Input & Capture',
      description: 'Gather descriptions from any source',
      completed: false,
      locked: true
    },
    {
      id: 3,
      name: 'plot-edit',
      label: 'Plot & Edit',
      description: 'Generate and edit your plot',
      completed: false,
      locked: true
    }
  ]
  private steps: WorkflowStep[] = this.standardSteps

  constructor(
    container: HTMLElement,
    appState: AppState,
    apiService: ApiService,
    toastService: Toast,
  ) {
    this.container = container
    this.appState = appState
    this.apiService = apiService
    this.toastService = toastService
    
    this.init()
  }

  private async init(): Promise<void> {
    // Ensure steps array matches the current mode
    this.steps = this.isAdvancedMode ? this.standardSteps : this.classicSteps
    
    // Set the initial advanced mode state in app state
    this.appState.setAdvancedMode(this.isAdvancedMode)
    
    this.setupEventListeners()
    this.setupSessionSubscription()
    await this.updateStepStatus()
    await this.render()
  }

  private setupEventListeners(): void {
    // Listen to app state changes to update step completion
    this.appState.subscribe('currentProject', async () => {
      await this.updateStepStatus()
      await this.render()
    })

    this.appState.subscribe('currentFile', async () => {
      await this.updateStepStatus()
      await this.render()
    })

    this.appState.subscribe('selections', async () => {
      await this.updateStepStatus()
      await this.render()
    })

    this.appState.subscribe('coordinates', async () => {
      await this.updateStepStatus()
      await this.render()
    })

    this.appState.subscribe('hasFormattedText', async () => {
      await this.updateStepStatus()
      await this.render()
    })
  }

  private async updateStepStatus(): Promise<void> {
    const project = this.appState.getCurrentProject()
    const selections = this.appState.getSelections()
    const coordinates = this.appState.getCoordinates()
    const hasFormattedText = this.appState.getHasFormattedText()
    const hasPDFs = project ? project.document_count > 0 : false

    // Debug logging for troubleshooting
    logger.debug('Workflow Navigation - Step Status Update:', {
      projectName: project?.name || 'None',
      projectId: project?.id || 'None',
      documentCount: project?.document_count || 0,
      hasPDFs,
      selectionsCount: selections.length,
      hasFormattedText,
      coordinatesCount: coordinates.length,
      isStandardMode: this.isAdvancedMode
    })

    if (this.isAdvancedMode) {
      // Standard mode step logic (3 steps)
      this.steps[0].completed = !!project
      // Step 2 completes ONLY after a snippet (selection) has been added
      this.steps[1].completed = this.steps[0].completed && (selections.length > 0)
      this.steps[2].completed = this.steps[1].completed && coordinates.length > 0

      // Lock status for standard mode
      this.steps[0].locked = false
      this.steps[1].locked = !this.steps[0].completed
      this.steps[2].locked = !this.steps[1].completed

      // Debug step status - Standard mode
      logger.debug('Standard Mode Step Status:', {
        step1: { completed: this.steps[0].completed, locked: this.steps[0].locked },
        step2: { completed: this.steps[1].completed, locked: this.steps[1].locked },
        step3: { completed: this.steps[2].completed, locked: this.steps[2].locked }
      })
    } else {
      // Classic mode step logic (4 steps)
      this.steps[0].completed = !!project
      this.steps[1].completed = this.steps[0].completed && selections.length > 0
      this.steps[2].completed = this.steps[1].completed && hasFormattedText
      this.steps[3].completed = this.steps[2].completed && coordinates.length > 0

      // Lock status for classic mode
      this.steps[0].locked = false
      this.steps[1].locked = !this.steps[0].completed
      this.steps[2].locked = !this.steps[1].completed
      this.steps[3].locked = !this.steps[2].completed

      // Debug step status - Classic mode
      logger.debug('Classic Mode Step Status:', {
        step1: { completed: this.steps[0].completed, locked: this.steps[0].locked },
        step2: { completed: this.steps[1].completed, locked: this.steps[1].locked },
        step3: { completed: this.steps[2].completed, locked: this.steps[2].locked },
        step4: { completed: this.steps[3].completed, locked: this.steps[3].locked }
      })
    }

    // Set current step automatically if user isn't ahead
    if (this.currentStep > this.getMaxUnlockedStep()) {
      this.currentStep = this.getMaxUnlockedStep()
    }

    await this.render()
  }


  public async navigateToStep(stepId: number): Promise<void> {
    const targetStep = this.steps.find(s => s.id === stepId)
    if (!targetStep || targetStep.locked) {
      this.toastService.error('Step Locked', 'Complete the previous steps first')
      return
    }

    // Auto-save current progress before switching
    await this.autoSave()

    this.currentStep = stepId
    
    // Emit step change event
    this.appState.setCurrentStep(stepId)
    
    await this.render()
  }

  private async autoSave(): Promise<void> {
    try {
      const project = this.appState.getCurrentProject()
      if (!project) return

             // Auto-save project data, selections, etc.
       const selections = this.appState.getSelections()
       if (selections.length > 0) {
         // Save selections to backend (only basic properties for SelectionCreate)
         for (const selection of selections) {
           if (!selection.id) {
             await this.apiService.createSelection({
               project_id: project.id,
               document_id: selection.document_id || '',
               document_type: selection.document_type || 'pdf',
               x: selection.x,
               y: selection.y,
               width: selection.width,
               height: selection.height,
               page_number: selection.page_number || 1,
               thumbnail_data: selection.thumbnail_data
             })
           }
         }
         
         // CRITICAL FIX: Refresh project data to update selection_count after auto-save
         console.log('🔄 Refreshing project data to update selection_count after auto-save')
         try {
           const updatedProject = await this.apiService.getProject(project.id)
           this.appState.setCurrentProject(updatedProject)
           console.log('✅ Project selection_count updated after auto-save:', updatedProject.selection_count)
         } catch (error) {
           console.error('❌ Failed to refresh project data after auto-save:', error)
         }
       }
    } catch (error) {
      console.error('Auto-save failed:', error)
      // Don't show error toast for auto-save failures to avoid interrupting UX
    }
  }

  public getCurrentStep(): number {
    return this.currentStep
  }

  public getStepName(): string {
    const step = this.steps.find(s => s.id === this.currentStep)
    return step?.name || 'unknown'
  }

  public canAdvanceToStep(stepId: number): boolean {
    const step = this.steps.find(s => s.id === stepId)
    return step ? !step.locked : false
  }

  private getMaxUnlockedStep(): number {
    let maxUnlocked = 1
    for (const step of this.steps) {
      if (!step.locked) {
        maxUnlocked = Math.max(maxUnlocked, step.id)
      }
    }
    return maxUnlocked
  }

  /**
   * Setup Better Auth session subscription for reactive updates
   * This follows Better Auth's recommended pattern for session management
   */
  private setupSessionSubscription(): void {
    // Use Better Auth's reactive session management
    // This automatically re-renders when session state changes (login/logout)
    this.sessionSubscription = authClient.useSession.subscribe(() => {
      // Re-render when session changes to update UI state
      this.render()
    })
  }

  private async render(): Promise<void> {
    const currentProject = this.appState.getCurrentProject()
    const projectName = currentProject ? currentProject.name : 'No Project Selected'
    
    // Get current session from Better Auth's reactive store
    // This eliminates the need for manual auth checks and prevents rate limiting
    const sessionAtom = authClient.useSession
    const sessionData = sessionAtom.get()
    const authStatus: AuthResponse = {
      authenticated: !!sessionData.data?.user,
      user: sessionData.data?.user ? {
        id: sessionData.data.user.id,
        email: sessionData.data.user.email,
        name: sessionData.data.user.name,
        emailVerified: sessionData.data.user.emailVerified,
        createdAt: sessionData.data.user.createdAt?.toISOString() || new Date().toISOString(),
        updatedAt: sessionData.data.user.updatedAt?.toISOString() || new Date().toISOString()
      } : undefined
    }
    
    this.container.innerHTML = `
      <nav class="workflow-nav">
        <!-- Top Row: Branding + Progress + Status + Logout -->
        <div class="workflow-header">
          <div class="workflow-branding">
            <h1 class="text-lg font-bold text-gray-900">
              📄 Doc2Plot <span class="text-sm font-normal text-gray-500">Alpha 0.5.0</span>
            </h1>
          </div>
          
          <div class="workflow-progress">
            ${this.steps.map((step, index) => {
              const stepHtml = `
                <div class="workflow-step ${step.id === this.currentStep ? 'active' : ''} ${step.completed ? 'completed' : ''} ${step.locked ? 'locked' : ''}">
                  <button 
                    class="step-button" 
                    data-step="${step.id}"
                    ${step.locked ? 'disabled' : ''}
                    title="${step.locked ? 'Complete previous steps first' : step.description}"
                  >
                    <div class="step-number">
                      ${step.id}
                    </div>
                  </button>
                  <div class="step-label">${step.label}</div>
                </div>
              `
              
              // Determine active path between steps
              const isActivePath = (this.currentStep > step.id)
              
              // Add connector after each step except the last one
              const connectorHtml = index < this.steps.length - 1 ? `
                <div class="workflow-connector">
                  <div class="connector-line ${step.completed ? 'completed' : ''} ${isActivePath ? 'active-path' : ''}"></div>
                </div>
              ` : ''
              
              return stepHtml + connectorHtml
            }).join('')}
          </div>
          
          <div class="workflow-status">
            ${authStatus.authenticated ? `
              <div class="flex items-center space-x-2 ml-6">
                <span class="text-sm text-gray-600">
                  ${authStatus.user?.name || authStatus.user?.email || 'User'}
                </span>
                <div class="relative">
                  <button 
                    id="user-menu-btn"
                    class="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-full transition-colors"
                    title="User Menu"
                  >
                    <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z"></path>
                    </svg>
                  </button>
                  <div id="user-menu-dropdown" class="hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                    <div class="py-1">
                      <button 
                        id="user-profile-btn"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"></path>
                        </svg>
                        Profile
                      </button>
                      <button 
                        id="settings-btn"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                        </svg>
                        Settings
                      </button>
                      <button 
                        id="logout-btn"
                        class="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        <svg class="w-4 h-4 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"></path>
                        </svg>
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ` : `
              <button 
                id="login-btn"
                class="ml-4 px-3 py-1 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded border transition-colors"
                title="Sign In"
              >
                Sign In
              </button>
            `}
          </div>
        </div>
        
        <!-- Bottom Row: Current Step Info + Project -->
        <div class="workflow-info">
          <div class="current-step-info">
            <h2>
              <span class="step-number-indicator">${this.currentStep}</span>
              ${this.steps[this.currentStep - 1]?.label}
            </h2>
            <p>${this.steps[this.currentStep - 1]?.description}</p>
          </div>
          <div class="project-name-display">
            <span class="project-label">Current Project:</span>
            <span class="project-name-text">${projectName}</span>
          </div>
        </div>
      </nav>
    `

    // Update main content top padding based on actual nav height
    const navEl = this.container.querySelector('.workflow-nav') as HTMLElement
    if (navEl) {
      const h = navEl.offsetHeight
      document.documentElement.style.setProperty('--workflow-nav-height', h + 'px')
      const main = document.querySelector('.main-content') as HTMLElement
      if (main) {
        main.style.paddingTop = `calc(var(--workflow-nav-height) + 16px)`
      }
    }

    this.attachEventListeners(authStatus)
  }

  private attachEventListeners(authStatus?: AuthResponse): void {
    // Step navigation buttons
    const stepButtons = this.container.querySelectorAll('.step-button')
    stepButtons.forEach(button => {
      button.addEventListener('click', (e) => {
        const stepId = parseInt((e.currentTarget as HTMLElement).dataset.step || '1')
        this.navigateToStep(stepId)
      })
    })

    // Authentication buttons
    const loginBtn = this.container.querySelector('#login-btn')
    if (loginBtn) {
      loginBtn.addEventListener('click', () => {
        this.showLoginModal()
      })
    }

    // User menu ellipses button
    const userMenuBtn = this.container.querySelector('#user-menu-btn')
    if (userMenuBtn) {
      userMenuBtn.addEventListener('click', (e) => {
        e.stopPropagation()
        this.toggleUserMenu()
      })
    }

    // User profile button in dropdown
    const userProfileBtn = this.container.querySelector('#user-profile-btn')
    if (userProfileBtn) {
      userProfileBtn.addEventListener('click', () => {
        if (authStatus?.authenticated && authStatus?.user) {
          showUserProfile(authStatus.user)
          this.hideUserMenu()
        }
      })
    }

    // Logout button in dropdown
    const logoutBtn = this.container.querySelector('#logout-btn')
    if (logoutBtn) {
      logoutBtn.addEventListener('click', () => {
        this.handleLogout()
        this.hideUserMenu()
      })
    }

    // Close user menu when clicking outside
    document.addEventListener('click', (e) => {
      const userMenu = this.container.querySelector('#user-menu-dropdown')
      const userMenuBtn = this.container.querySelector('#user-menu-btn')
      if (userMenu && !userMenu.contains(e.target as Node) && !userMenuBtn?.contains(e.target as Node)) {
        this.hideUserMenu()
      }
    })

    // Settings button in dropdown
    const settingsBtn = this.container.querySelector('#settings-btn')
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        this.showSettingsModal()
        this.hideUserMenu()
      })
    }

  }

  // Authentication methods
  private showLoginModal(): void {
    import('./auth-modal').then(({ showAuthModal }) => {
      showAuthModal(true, async () => {
        // Refresh the navigation after successful login
        await this.render()
        this.attachEventListeners()
      })
    })
  }

  private showSettingsModal(): void {
    showSettingsModal(this.appState)
  }

  private async handleLogout(): Promise<void> {
    console.log('🔴 LOGOUT: WorkflowNav handleLogout() called!')
    
    console.log('🔴 LOGOUT: Signing out...')
    
    try {
      // Sign out via Better Auth (clears cookies)
      await signOut()
      console.log('🎉 Logout successful')
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      // Immediate redirect
      console.log('🔴 LOGOUT: Redirecting to login page...')
      window.location.href = '/'
    }
  }

  // Public methods for external components
  public async refresh(): Promise<void> {
    // Sync the internal mode with app state
    this.isAdvancedMode = this.appState.getAdvancedMode()
    this.steps = this.isAdvancedMode ? this.standardSteps : this.classicSteps
    
    await this.updateStepStatus()
    await this.render()
  }


  public async markStepComplete(stepId: number): Promise<void> {
    const step = this.steps.find(s => s.id === stepId)
    if (step) {
      step.completed = true
      await this.updateStepStatus()
      await this.render()
    }
  }

  public updateConnectionStatus(isConnected: boolean): void {
    // Update connection status in the user profile modal if it's open
    updateProfileConnectionStatus(isConnected)
  }

  private toggleUserMenu(): void {
    const dropdown = this.container.querySelector('#user-menu-dropdown')
    if (dropdown) {
      dropdown.classList.toggle('hidden')
    }
  }

  private hideUserMenu(): void {
    const dropdown = this.container.querySelector('#user-menu-dropdown')
    if (dropdown) {
      dropdown.classList.add('hidden')
    }
  }

  /**
   * Clean up session subscription to prevent memory leaks
   * Call this when the component is destroyed
   */
  public destroy(): void {
    if (this.sessionSubscription) {
      this.sessionSubscription()
      this.sessionSubscription = null
    }
  }

} 